import { Form, Formik } from "formik";
import plur from "plur";
import { Button, Flex, Label, Link, ModalV2, Text } from "@jsq/ds/core";
import { FormSelect, FormDatePicker } from "@jsq/ds/forms";
import { getMinSelectableDate } from "~/pages/UserAndRoles/utils/dateFormatter";
import {
  useFetchWorkspaces,
  useFetchWorkspaceRoles,
} from "~/pages/UserAndRoles/WorkspacesPage/hooks";
import { SPACING } from "~/styles/variables";
import { useAssignToWorkspaceModal } from "./hooks";
import { validationSchema } from "./schema";
import type { ArenaAccessRow } from "~/pages/Dashboard/components/WorkspaceTable";

export type AssignToWorkspaceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  userIds?: number[] | string[];
  userId?: string;
  userName?: string;
  existingAssignments?: ArenaAccessRow[];
};

export const AssignToWorkspaceModal = ({
  isOpen,
  onClose,
  userId,
  userName,
  userIds,
  existingAssignments = [],
}: AssignToWorkspaceModalProps) => {
  const { initialValues, createWorkspaceAccess, loading } = useAssignToWorkspaceModal({
    userIds: userId ? [userId] : userIds!,
    onClose,
  });
  const { rowData: workspaces } = useFetchWorkspaces({});
  const { roles } = useFetchWorkspaceRoles();

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={createWorkspaceAccess}
      validationSchema={validationSchema(existingAssignments)}
    >
      {({ handleSubmit, isSubmitting, resetForm }) => {
        const handleClose = () => {
          resetForm();
          onClose();
        };

        return (
          <ModalV2
            title={`Assign ${userId ? userName : userIds?.length + " " + plur("user", userIds?.length)} to workspace`}
            description={
              <Link
                href="/canopy/users/workspaces"
                variant="primary"
                icon="external-link"
                shouldOpenInNewTab
                iconPosition="right"
                onClick={(e) => {
                  e.preventDefault();
                  window.open("/canopy/users/workspaces", "_blank");
                }}
                align="left"
              >
                View workspaces
              </Link>
            }
            show={isOpen}
            onHide={handleClose}
            data-testid="assign-to-workspace-modal"
          >
            <Form>
              <ModalV2.Body>
                <Flex flexDirection="column" gap={SPACING.LARGE}>
                  <FormSelect
                    data-testid="workspace-select"
                    label="Workspace"
                    name="arenaId"
                    placeholder="Select workspace..."
                    full
                    hideSelectAll
                    hideSelectNone
                  >
                    <FormSelect.Section>
                      {workspaces.map((workspace) => (
                        <FormSelect.Option key={workspace?.id} value={workspace?.id}>
                          {`${workspace?.name} (${workspace?.id})`}
                        </FormSelect.Option>
                      ))}
                    </FormSelect.Section>
                  </FormSelect>

                  <FormSelect
                    data-testid="role-select"
                    label="Role"
                    name="roleGroupName"
                    placeholder="Select role..."
                    full
                    hideSelectAll
                    hideSelectNone
                    matchFromStart={false}
                  >
                    <FormSelect.Section>
                      {roles.map((r) => (
                        <FormSelect.Option key={r?.role} value={r?.role}>
                          {r?.role}
                        </FormSelect.Option>
                      ))}
                    </FormSelect.Section>
                  </FormSelect>

                  <FormSelect
                    data-testid="access-type-select"
                    label="Access type"
                    name="accessType"
                    placeholder="Select access type..."
                    full
                    hideSelectAll
                    hideSelectNone
                  >
                    <FormSelect.Section>
                      <FormSelect.Option value="named">Named access</FormSelect.Option>
                      <FormSelect.Option value="unnamed">Anonymous access</FormSelect.Option>
                    </FormSelect.Section>
                  </FormSelect>

                  <Flex flexDirection="column" data-testid="expiration-date-picker">
                    <Label>Expiration date</Label>
                    <FormDatePicker
                      name="expirationDate"
                      hasIcon
                      placeholderText="MM/DD/YYYY"
                      dateFormat="MM/dd/yyyy"
                      isFull
                      minDate={getMinSelectableDate()}
                    />
                    <Text variant="secondary-sm">Optional</Text>
                  </Flex>

                  <Flex gap={SPACING.MEDIUM} justifyContent="end">
                    <Button variant="neutral" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      onClick={() => handleSubmit()}
                      variant="primary"
                      isLoading={isSubmitting || loading}
                      disabled={isSubmitting || loading}
                    >
                      {userId
                        ? "Add to workspace"
                        : `Add ${userIds?.length} ${plur("user", userIds?.length)} to workspace`}
                    </Button>
                  </Flex>
                </Flex>
              </ModalV2.Body>
            </Form>
          </ModalV2>
        );
      }}
    </Formik>
  );
};
